import React, { useState } from 'react';
import { LauncherLayout } from "../layout/LauncherLayout";
import { EnsureLoggedIn } from "../../admin/EnsureLoggedIn";
import {
    Card, Row, Col, Typography, Spin, Tag, Space, Button, Flex,
    Empty, Breadcrumb, Pagination, Segmented, Grid
} from "antd";
import {
    SendOutlined, BookOutlined, UserOutlined,
    EnvironmentOutlined, CheckCircleOutlined,
    ClockCircleOutlined, CloseCircleOutlined, ArrowRightOutlined,
    TeamOutlined, ArrowLeftOutlined, CalendarOutlined
} from "@ant-design/icons";
import { launcherCard } from "../common/styles";
import { useQuery } from "react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { OrganizerInvitationSummaryDto, Page, InvitationStatus } from "../../commons/data/types";
import dayjs from "dayjs";

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

export const OrganizerInvitationsPage: React.FC = () => {
    return (
        <LauncherLayout>
            <EnsureLoggedIn shouldBeOrganizer={true}>
                <OrganizerInvitationsContent />
            </EnsureLoggedIn>
        </LauncherLayout>
    );
};

const OrganizerInvitationsContent: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const [page, setPage] = useState<number>(1);
    const pageSize = 9;
    const [statusFilter, setStatusFilter] = useState<string>('ALL');

    // Build query URL with pagination and status
    const buildQueryUrl = () => {
        const params = new URLSearchParams();
        params.append('page', (page - 1).toString());
        params.append('size', pageSize.toString());
        if (statusFilter === 'UNPLANNED' || statusFilter === 'SCHEDULED' || statusFilter === 'CANCELLED') {
            params.append('status', statusFilter);
        }
        return `/api/organizer/invitations?${params.toString()}`;
    };

    const { data, isLoading } = useQuery<Page<OrganizerInvitationSummaryDto>>(
        ['organizer-invitations', page, pageSize, statusFilter],
        () => axios.get(buildQueryUrl()).then(res => res.data),
        { keepPreviousData: true }
    );

    const rawInvitations = data?.content || [];

    // Filter client-side for 'READY' (Ready to Schedule) filter if selected
    const filteredInvitations = statusFilter === 'READY'
        ? rawInvitations.filter(inv => inv.readyToSchedule)
        : rawInvitations;

    const handleFilterChange = (val: string) => {
        setStatusFilter(val);
        setPage(1);
    };

    return (
        <div style={{ maxWidth: '100vw', margin: '30px auto', padding: '0 20px' }}>
            {/* BREADCRUMB & BACK ACTION */}
            <div style={{ marginBottom: 16 }}>
                <Flex justify="space-between" align="center" wrap="wrap" gap="small">
                    <Breadcrumb
                        items={[
                            { title: <Link to="/organizer">{t('organizer.home.title', 'Organizer')}</Link> },
                            { title: t('organizer.invitations.manageTitle', 'Manage Invitations') }
                        ]}
                    />
                    <Space wrap>
                        <Button icon={<BookOutlined />} onClick={() => navigate('/organizer/adventures')}>
                            {t('organizer.adventures.title', 'Manage Adventures')}
                        </Button>
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/organizer')}>
                            {t('organizer.home.title', 'Organizer Home')}
                        </Button>
                    </Space>
                </Flex>
            </div>

            {/* HERO / HEADER CARD */}
            <Card style={{ ...launcherCard, marginBottom: 24 }}>
                <Flex justify="space-between" align="center" wrap="wrap" gap="middle">
                    <div>
                        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <SendOutlined style={{ color: '#52c41a' }} />
                            {t('organizer.invitations.pageTitle', 'Invitations Management')}
                        </Title>
                        <Paragraph style={{ color: '#8c8c8c', margin: '6px 0 0 0' }}>
                            {t('organizer.invitations.pageDesc', 'View and manage all your adventure invitations, check player quorums, and review join requests.')}
                        </Paragraph>
                    </div>
                </Flex>
            </Card>

            {/* FILTER CONTROLS */}
            <Card style={{ ...launcherCard, marginBottom: 24, padding: '12px 16px' }}>
                <Flex
                    justify={isMobile ? "flex-start" : "space-between"}
                    align={isMobile ? "stretch" : "center"}
                    vertical={isMobile}
                    gap="small"
                >
                    <Text strong style={{ fontSize: 13, marginBottom: isMobile ? 4 : 0 }}>
                        {t('common.filter', 'Filter')}:
                    </Text>
                    <Segmented
                        vertical={isMobile}
                        block={isMobile}
                        value={statusFilter}
                        onChange={(val) => handleFilterChange(val as string)}
                        options={[
                            { label: t('organizer.invitations.filter.all', 'All Invitations'), value: 'ALL' },
                            { label: t('organizer.invitations.filter.ready', 'Ready to Schedule'), value: 'READY', icon: <CheckCircleOutlined style={{ color: '#52c41a' }} /> },
                            { label: t('organizer.invitations.filter.unplanned', 'Unplanned'), value: 'UNPLANNED' },
                            { label: t('organizer.invitations.filter.scheduled', 'Scheduled'), value: 'SCHEDULED' },
                            { label: t('organizer.invitations.filter.cancelled', 'Cancelled'), value: 'CANCELLED' }
                        ]}
                    />
                </Flex>
            </Card>

            {/* INVITATIONS LIST */}
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Spin size="large" />
                </div>
            ) : filteredInvitations.length > 0 ? (
                <>
                    <Row gutter={[24, 24]}>
                        {filteredInvitations.map((inv) => (
                            <Col xs={24} sm={12} lg={8} key={inv.id}>
                                <OrganizerInvitationCard
                                    invitation={inv}
                                    onClick={() => navigate(`/organizer/invitations/${inv.id}/join-requests`)}
                                />
                            </Col>
                        ))}
                    </Row>

                    {/* PAGINATION */}
                    {data && data.totalPages > 1 && (
                        <div style={{ marginTop: 32, textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                            <Pagination
                                current={page}
                                pageSize={pageSize}
                                total={data.totalElements}
                                onChange={(newPage) => setPage(newPage)}
                                showSizeChanger={false}
                            />
                        </div>
                    )}
                </>
            ) : (
                <Card style={{ ...launcherCard, textAlign: 'center', padding: '48px 0' }}>
                    <Empty
                        description={
                            <div>
                                <Paragraph strong style={{ fontSize: 16, marginBottom: 4 }}>
                                    {t('organizer.invitations.none', 'No Invitations Found')}
                                </Paragraph>
                                <Paragraph type="secondary">
                                    {t('organizer.invitations.noneDesc', "You haven't created any adventure invitations yet. Go to Manage Adventures to create one.")}
                                </Paragraph>
                            </div>
                        }
                    >
                        <Button type="primary" icon={<BookOutlined />} onClick={() => navigate('/organizer/adventures')}>
                            {t('organizer.invitations.createPrompt', 'Go to Adventures')}
                        </Button>
                    </Empty>
                </Card>
            )}
        </div>
    );
};

interface OrganizerInvitationCardProps {
    invitation: OrganizerInvitationSummaryDto;
    onClick: () => void;
}

const OrganizerInvitationCard: React.FC<OrganizerInvitationCardProps> = ({ invitation, onClick }) => {
    const { t } = useTranslation();

    const getStatusTag = (status: InvitationStatus) => {
        switch (status) {
            case 'SCHEDULED':
                return <Tag color="green" icon={<CheckCircleOutlined />}>{t('organizer.invitations.status.scheduled', 'Scheduled')}</Tag>;
            case 'CANCELLED':
                return <Tag color="red" icon={<CloseCircleOutlined />}>{t('organizer.invitations.status.cancelled', 'Cancelled')}</Tag>;
            case 'UNPLANNED':
            default:
                return <Tag color="blue" icon={<ClockCircleOutlined />}>{t('organizer.invitations.status.unplanned', 'Unplanned')}</Tag>;
        }
    };

    const totalConfirmed = invitation.preexistingPlayers + invitation.approvedRequestsCount;
    const isReadyToSchedule = invitation.readyToSchedule;

    return (
        <Card
            hoverable
            onClick={onClick}
            style={{
                ...launcherCard,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: '12px',
                border: isReadyToSchedule ? '2px solid #52c41a' : undefined,
                transition: 'all 0.25s ease-in-out',
                position: 'relative'
            }}
            bodyStyle={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                padding: '16px'
            }}
            cover={
                <div style={{ height: '170px', overflow: 'hidden', position: 'relative', background: '#141414' }}>
                    {invitation.adventureCoverUrl ? (
                        <img
                            alt={invitation.adventureTitle || 'Adventure Cover'}
                            src={invitation.adventureCoverUrl}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <BookOutlined style={{ fontSize: '48px', color: '#555' }} />
                        </div>
                    )}

                    {/* TOP STATUS BADGES */}
                    <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {getStatusTag(invitation.status)}
                        {invitation.published ? (
                            <Tag color="cyan">Published</Tag>
                        ) : (
                            <Tag color="default">Draft</Tag>
                        )}
                    </div>

                    {/* READY TO SCHEDULE BADGE / NOTIFICATION DOT */}
                    {isReadyToSchedule && (
                        <div style={{ position: 'absolute', top: 10, right: 10 }}>
                            <Tag
                                color="#52c41a"
                                icon={<CheckCircleOutlined />}
                                style={{
                                    fontWeight: 600,
                                    boxShadow: '0 2px 8px rgba(82, 196, 26, 0.4)',
                                    padding: '2px 8px',
                                    borderRadius: '6px'
                                }}
                            >
                                {t('organizer.invitations.readyToSchedule', 'Ready to Schedule')}
                            </Tag>
                        </div>
                    )}
                </div>
            }
            actions={[
                <div style={{ padding: '0 12px' }} key="action-bar">
                    <Button
                        type={isReadyToSchedule ? "primary" : "default"}
                        block
                        icon={<ArrowRightOutlined />}
                        onClick={onClick}
                        style={{
                            borderRadius: '8px',
                            background: isReadyToSchedule ? '#52c41a' : undefined,
                            borderColor: isReadyToSchedule ? '#52c41a' : undefined,
                            fontWeight: 500
                        }}
                    >
                        {t('organizer.invitations.viewJoinRequests', 'View Join Requests')}
                    </Button>
                </div>
            ]}
        >
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {/* ADVENTURE TITLE */}
                <Title level={4} style={{ marginTop: 0, marginBottom: 6 }} ellipsis={{ rows: 1 }}>
                    {invitation.adventureTitle || 'Untitled Adventure'}
                </Title>

                {/* GAME SYSTEM */}
                <div style={{ marginBottom: 8 }}>
                    <Space size="small" align="center">
                        {invitation.systemMediaUrl && (
                            <img src={invitation.systemMediaUrl} alt="" style={{ width: 16, height: 16, borderRadius: 2 }} />
                        )}
                        <Text type="secondary" strong style={{ fontSize: 13 }}>
                            {invitation.systemTitle || 'TTRPG'}
                        </Text>
                    </Space>
                </div>

                {/* GM & PLAY SPACE */}
                <div style={{ marginBottom: 12, fontSize: 13 }}>
                    {invitation.gmName && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <UserOutlined style={{ color: '#1890ff' }} />
                            <Text type="secondary" ellipsis={{ tooltip: invitation.gmName }}>
                                GM: {invitation.gmName}
                            </Text>
                        </div>
                    )}
                    {invitation.playSpaceName && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <EnvironmentOutlined style={{ color: '#52c41a' }} />
                            <Text type="secondary" ellipsis={{ tooltip: invitation.playSpaceName }}>
                                {invitation.playSpaceName}
                            </Text>
                        </div>
                    )}
                </div>

                {/* SESSIONS SCHEDULE (FROM - TO READ ONLY PANEL) */}
                <div
                    style={{
                        background: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        marginBottom: 12,
                        border: '1px solid rgba(0, 0, 0, 0.06)'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Space size={6} align="center">
                            <CalendarOutlined style={{ color: '#1890ff', fontSize: 13 }} />
                            <Text strong style={{ fontSize: 12 }}>
                                {t('organizer.invitations.schedule', 'Schedule')}
                            </Text>
                        </Space>
                        {invitation.sessionsCount > 0 && (
                            <Tag style={{ margin: 0, fontSize: 11, padding: '0 6px', borderRadius: 4 }}>
                                {invitation.sessionsCount} {invitation.sessionsCount === 1 ? t('organizer.invitations.singleSession', 'session') : t('organizer.invitations.multipleSessions', 'sessions')}
                            </Tag>
                        )}
                    </div>
                    {invitation.firstSessionTime ? (
                        invitation.firstSessionTime === invitation.lastSessionTime ? (
                            <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text type="secondary">{t('organizer.invitations.sessionDate', 'Date')}:</Text>
                                <Text strong style={{ fontSize: 12 }}>{dayjs(invitation.firstSessionTime).format('YYYY-MM-DD HH:mm')}</Text>
                            </div>
                        ) : (
                            <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text type="secondary">{t('organizer.invitations.from', 'From')}:</Text>
                                    <Text strong style={{ fontSize: 12 }}>{dayjs(invitation.firstSessionTime).format('YYYY-MM-DD HH:mm')}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Text type="secondary">{t('organizer.invitations.to', 'To')}:</Text>
                                    <Text strong style={{ fontSize: 12 }}>{dayjs(invitation.lastSessionTime).format('YYYY-MM-DD HH:mm')}</Text>
                                </div>
                            </div>
                        )
                    ) : (
                        <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                            {t('organizer.invitations.noSessionsScheduled', 'No sessions scheduled yet')}
                        </Text>
                    )}
                </div>

                {/* PLAYER QUORUM STATUS */}
                <div
                    style={{
                        background: isReadyToSchedule ? 'rgba(82, 196, 26, 0.12)' : 'rgba(0, 0, 0, 0.03)',
                        borderRadius: '8px',
                        padding: '8px 12px',
                        marginBottom: 12,
                        border: isReadyToSchedule ? '1px solid #b7eb8f' : '1px solid rgba(0, 0, 0, 0.06)'
                    }}
                >
                    <Flex justify="space-between" align="center">
                        <Text strong style={{ fontSize: 12, color: isReadyToSchedule ? '#389e0d' : '#595959' }}>
                            <TeamOutlined style={{ marginRight: 5 }} />
                            {t('organizer.invitations.playersQuorum', 'Players')}: {totalConfirmed} / {invitation.maxTotalPlayers}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            (Min: {invitation.minTotalPlayers})
                        </Text>
                    </Flex>
                    {isReadyToSchedule && (
                        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                            <Text style={{ fontSize: 11, color: '#389e0d', fontWeight: 600 }}>
                                {t('organizer.invitations.readyToScheduleDesc', 'Minimum player quorum reached!')}
                            </Text>
                        </div>
                    )}
                </div>

                {/* JOIN REQUESTS COUNTERS */}
                <div style={{ marginTop: 'auto', paddingTop: 8 }}>
                    <Text strong style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginBottom: 6 }}>
                        {t('organizer.joinRequests.title', 'Join Requests')}:
                    </Text>
                    <Flex gap="small" wrap="wrap">
                        <Tag color="gold" style={{ margin: 0, padding: '2px 8px', borderRadius: '4px' }}>
                            <ClockCircleOutlined style={{ marginRight: 4 }} />
                            {invitation.pendingRequestsCount} {t('joinRequests.status.pending', 'Pending')}
                        </Tag>
                        <Tag color="green" style={{ margin: 0, padding: '2px 8px', borderRadius: '4px' }}>
                            <CheckCircleOutlined style={{ marginRight: 4 }} />
                            {invitation.approvedRequestsCount} {t('joinRequests.status.approved', 'Approved')}
                        </Tag>
                        <Tag color="red" style={{ margin: 0, padding: '2px 8px', borderRadius: '4px' }}>
                            <CloseCircleOutlined style={{ marginRight: 4 }} />
                            {invitation.rejectedRequestsCount} {t('joinRequests.status.rejected', 'Declined')}
                        </Tag>
                    </Flex>
                </div>
            </div>
        </Card>
    );
};
